import { ca, enGB, es, type Locale } from 'date-fns/locale';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { type DayButton, DayPicker } from 'react-day-picker';
import { DEFAULT_LANGUAGE } from '@avanta/shared/types/patients';
import { cn } from '@avanta/shared/utils/cn';
import {
  calendarCaptionLabel,
  calendarChevron,
  calendarContainer,
  calendarDayButton,
  calendarWeekNumber,
  getCalendarStyles,
} from './styles';
import './styles.css';
import { type CalendarProps, type ChevronIconProps } from './types';
import { createCalendarFormatters } from './utils';

const translations: Record<string, Locale> = { es, en: enGB, ca };

const ChevronIcon = ({
  className,
  orientation,
  ...props
}: ChevronIconProps): React.ReactElement => {
  if (orientation === 'left') {
    return <ChevronLeftIcon className={cn(calendarChevron, className)} {...props} />;
  }

  if (orientation === 'right') {
    return <ChevronRightIcon className={cn(calendarChevron, className)} {...props} />;
  }

  return <ChevronDownIcon className={cn(calendarChevron, className)} {...props} />;
};

const CalendarDayButton = ({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>): React.ReactElement => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const dayButtonClasses = cn(calendarDayButton, className);
  const dataSelectedSingle =
    modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle;

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={dataSelectedSingle}
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      data-today={modifiers.today && !modifiers.selected}
      data-outside={modifiers.outside}
      className={dayButtonClasses}
      {...props}
    />
  );
};

export const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  appLanguage = DEFAULT_LANGUAGE,
  formatters,
  components,
  ...props
}: CalendarProps): React.ReactElement => {
  const calendarStyles = getCalendarStyles(buttonVariant);
  const locale = translations[appLanguage || DEFAULT_LANGUAGE];

  return (
    <DayPicker
      mode="single"
      showOutsideDays={showOutsideDays}
      className={cn(calendarContainer, className)}
      locale={locale}
      captionLayout={captionLayout}
      {...props}
      formatters={{
        ...createCalendarFormatters(locale),
        ...formatters,
      }}
      classNames={{
        ...calendarStyles,
        caption_label: calendarCaptionLabel({
          layout: captionLayout === 'label' ? 'label' : 'dropdown',
        }),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => (
          <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />
        ),
        Chevron: ChevronIcon,
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => (
          <td {...props}>
            <div className={calendarWeekNumber}>{children}</div>
          </td>
        ),
        ...components,
      }}
    />
  );
};
